import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    password: '',
    password_confirmation: ''
  });
  const [countryCode, setCountryCode] = useState('+974'); // Qatar by default
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const countryCodes = [
    { code: '+974', country: 'QA', name: 'Qatar', flag: '🇶🇦' },
    { code: '+971', country: 'AE', name: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+965', country: 'KW', name: 'Kuwait', flag: '🇰🇼' },
    { code: '+973', country: 'BH', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+968', country: 'OM', name: 'Oman', flag: '🇴🇲' },
    { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
    { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirmation) {
      toast.error('Passwords do not match!');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    // Combine country code with mobile number if mobile is provided
    const submissionData = {
      ...formData,
      mobile: formData.mobile ? `${countryCode}${formData.mobile}` : ''
    };
    const result = await register(submissionData, false);
    
    if (result.success) {
      navigate('/login');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-6 text-purple-500 hover:text-opacity-80 hover:!text-purple-600 transition">
            <svg className="w-8 h-8 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-lg font-medium">Back to Home</span>
          </Link>
          <h1 className="text-4xl font-bold text-black mb-2">Create Your Account</h1>
          <p className="text-body text-lg">Join thousands discovering amazing events</p>
        </div>

        {/* Register Card */}
        <div className="rounded-sm border border-stroke bg-white shadow-default">
          <div className="border-b border-stroke px-7 py-4">
            <h3 className="text-xl font-semibold text-black">Personal Information</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="p-7">
            <div className="mb-5.5">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5.5 mb-5.5">
                <div>
                  <label className="form-label">
                    First Name <span className="text-meta-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    className="form-control"
                    placeholder="Enter your first name"
                    value={formData.first_name}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="form-label">
                    Last Name <span className="text-meta-1">*</span>
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    className="form-control"
                    placeholder="Enter your last name"
                    value={formData.last_name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="mb-5.5">
                <label className="form-label">
                  Email Address <span className="text-meta-1">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="form-control"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              {/* Mobile with Country Code */}
              <div className="mb-5.5">
                <label className="form-label">
                  Mobile Number <span className="text-bodydark2">(Optional)</span>
                </label>
                <div className="flex gap-3">
                  <div style={{ width: '130px' }}>
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="form-control text-sm"
                      style={{ fontSize: '1rem', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code} style={{ fontSize: '1rem' }}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1">
                    <input
                      type="tel"
                      name="mobile"
                      className="form-control"
                      placeholder="12345678"
                      value={formData.mobile}
                      onChange={handleChange}
                      pattern="[0-9]*"
                      maxLength="15"
                    />
                  </div>
                </div>
                <p className="text-sm text-bodydark2 mt-1">Enter your mobile number without country code</p>
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5.5 mb-5.5">
                <div>
                  <label className="form-label">
                    Password <span className="text-meta-1">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    required
                    className="form-control"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label className="form-label">
                    Confirm Password <span className="text-meta-1">*</span>
                  </label>
                  <input
                    type="password"
                    name="password_confirmation"
                    required
                    className="form-control"
                    placeholder="Re-enter password"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="mb-5.5 flex items-start">
                <input
                  id="terms"
                  type="checkbox"
                  required
                  className="w-4 h-4 mt-1 border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="terms" className="ml-3 text-sm text-body">
                  I agree to the{' '}
                  <Link to="/terms" className="text-purple-500 hover:underline font-medium hover:!text-purple-500">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-purple-500 hover:underline font-medium hover:!text-purple-500">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col gap-3">
              <button
                type="submit"
                disabled={loading}
                className="btn !bg-purple-500 pt-3 pb-3 font-weight-bold !text-white w-full justify-center hover:!bg-purple-600"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </>
                ) : (
                  <>
                    Create My Account
                  </>
                )}
              </button>

              <div className="text-center">
                <span className="text-sm text-body">Already have an account? </span>
                <Link to="/login" className="text-sm text-purple-500 hover:underline hover:!text-purple-500 font-medium">
                  Sign In
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;

